import { yupResolver } from '@hookform/resolvers/yup';
import { Command, CornerDownLeft } from '@untitled-ui/icons-react/build/cjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import { REGULAR_EXPRESSION_NEW } from 'src/constants/common';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { usePhone } from 'src/hooks/usePhone';
import { createShipperContact, updateShipperContact } from 'src/services/ShipperService';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

interface IFormData {
    contactName: string;
    email: string;
    phoneNumber: string;
}

interface ShipperContactFormProps {
    handleEditContact: (isEdit?: boolean) => () => void;
    contactId?: string;
    id?: number;
    shipperContacts: any[];
    handleSaveContact: (data: any) => void;
}

const ShipperContactForm = ({ handleEditContact, contactId, id, shipperContacts, handleSaveContact }: ShipperContactFormProps) => {
    const { t } = useTranslation();
    const { getShipper } = useAddressDetails();
    const { isPhoneValid } = usePhone();
    const [isPhoneNumberValue, setIsPhoneNumberValue] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const saveButtonRef = useRef<HTMLDivElement>(null);

    const validationSchema = Yup.object().shape({
        contactName: Yup.string().required('Contact name is required'),
        email: Yup.string()
            .test('email-or-phone-required', 'Email or phone number is required', function (value) {
                const { phoneNumber } = this.parent;

                return !!(value || phoneNumber);
            })
            .test('valid-email-format', t('ValidationMessages.Invalid_email_format'), function (value) {
                if (!value || value.trim() === '') return true;

                return REGULAR_EXPRESSION_NEW.EMAIL.test(value);
            }),
        phoneNumber: Yup.string()
            .trim()
            .test('phone-or-email-required', 'Email or phone number is required', function (value) {
                const { email } = this.parent;

                return !!(value || email);
            })
            .test('is-valid-phone-if-provided', 'PhoneNumber must include digits beyond the country code', (value) => {
                if (!value || value.trim() === '') return true;
                if (!/^\+\d{1,4}\d+$/.test(value)) return false;

                const phoneNumber = parsePhoneNumberFromString(value);

                if (!phoneNumber || !phoneNumber.country) {
                    return false;
                }

                return isPhoneValid(phoneNumber.nationalNumber, phoneNumber.country);
            }),
    }) as Yup.ObjectSchema<IFormData>;

    const { control, watch, setValue, handleSubmit, clearErrors, reset } = useForm<IFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onSubmit',
        defaultValues: {
            contactName: '',
            email: '',
            phoneNumber: '',
        },
    });

    const watchContactName = watch('contactName');
    const watchEmail = watch('email');
    const watchPhoneNumber = watch('phoneNumber');

    // Store initial form values
    const initialValuesRef = useRef<IFormData | null>(null);

    useEffect(() => {
        if (contactId) {
            // Convert contactId to number for comparison since contact.id is a number
            const contactIdNum = typeof contactId === 'string' ? parseInt(contactId) : contactId;
            const contactInfo = shipperContacts.find((contact: any) => contact.id === contactIdNum);

            if (contactInfo) {
                const phoneNumber = contactInfo.number && !contactInfo.number.startsWith('+') ? `+${contactInfo.number}` : contactInfo.number || '';

                const initialFormData: IFormData = {
                    contactName: contactInfo.name || '',
                    email: contactInfo.email || '',
                    phoneNumber: phoneNumber,
                };

                reset(initialFormData);
                setIsPhoneNumberValue(true);
                // Store initial values for change detection
                initialValuesRef.current = {
                    contactName: contactInfo.name || '',
                    email: contactInfo.email || '',
                    phoneNumber: phoneNumber,
                };
            }
        } else {
            const emptyFormData: IFormData = {
                contactName: '',
                email: '',
                phoneNumber: '',
            };
            reset(emptyFormData);
            // Store initial values for change detection
            initialValuesRef.current = {
                contactName: '',
                email: '',
                phoneNumber: '',
            };
        }
    }, [contactId, shipperContacts, reset]);

    // Compare current values with initial values
    const hasFormChanged = useMemo(() => {
        if (initialValuesRef.current === null) {
            return false;
        }

        return watchContactName !== initialValuesRef.current.contactName || watchEmail !== initialValuesRef.current.email || watchPhoneNumber !== initialValuesRef.current.phoneNumber;
    }, [watchContactName, watchEmail, watchPhoneNumber]);

    useEffect(() => {
        // Scroll to save button when form opens (on mount or when contactId changes)
        const scrollToButton = () => {
            const element = saveButtonRef.current;

            if (element) {
                // Find the scrollable parent container
                let scrollableParent: HTMLElement | null = element.parentElement;

                while (scrollableParent) {
                    const style = window.getComputedStyle(scrollableParent);
                    const overflow = style.overflow + style.overflowY + style.overflowX;

                    if (overflow.includes('auto') || overflow.includes('scroll') || scrollableParent === document.body || scrollableParent === document.documentElement) {
                        break;
                    }

                    scrollableParent = scrollableParent.parentElement;
                }

                const rect = element.getBoundingClientRect();
                const scrollableElement = scrollableParent && scrollableParent !== document.body ? scrollableParent : window;

                if (scrollableElement === window) {
                    // Scroll window
                    const elementPosition = rect.top;
                    const offsetPosition = elementPosition + window.scrollY - 150; // 150px offset from top

                    window.scrollTo({
                        top: Math.max(0, offsetPosition),
                        behavior: 'smooth',
                    });
                } else if (scrollableParent) {
                    // Scroll container
                    const containerRect = scrollableParent.getBoundingClientRect();
                    const elementTop = rect.top - containerRect.top;
                    const scrollTop = scrollableParent.scrollTop + elementTop - 150; // 150px offset from top

                    scrollableParent.scrollTo({
                        top: Math.max(0, scrollTop),
                        behavior: 'smooth',
                    });
                } else {
                    // Fallback: use scrollIntoView
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        };

        // Use longer timeout to ensure DOM is fully rendered
        const timeoutId = setTimeout(scrollToButton, 200);

        return () => clearTimeout(timeoutId);
    }, [contactId]);

    const handleContactNameChange = (e: any) => {
        setValue('contactName', e.target.value, { shouldDirty: true });
        clearErrors('contactName');
    };

    const handlePhoneNumberChange = (_: any, phone: any, code: any) => {
        // Only set value if there's an actual phone number, not just country code
        if (phone && phone.trim() !== '') {
            setValue('phoneNumber', `+${code}${phone}`, { shouldDirty: true });
        } else {
            setValue('phoneNumber', '', { shouldDirty: true });
        }
        clearErrors('phoneNumber');
        clearErrors('email');
    };

    const handleEmailChange = (e: any) => {
        setValue('email', e.target.value, { shouldDirty: true });
        clearErrors('email');
        clearErrors('phoneNumber');
    };

    const handleSave = async (data: IFormData) => {
        if (!id) {
            return;
        }

        const payload = {
            shipperId: +id,
            contactName: data.contactName,
            email: data.email,
            phoneNumber: data.phoneNumber,
        };

        try {
            if (contactId) {
                setIsLoading(true);
                await updateShipperContact(+contactId, payload)
                    .then((res) => {
                        setIsLoading(false);

                        if (res?.data) {
                            getShipper();
                            handleSaveContact(res);
                            // Reset form with saved values so it's no longer dirty
                            const savedValues = {
                                contactName: data.contactName,
                                email: data.email,
                                phoneNumber: data.phoneNumber,
                            };
                            reset(savedValues);
                            // Update initial values so form is no longer considered changed
                            initialValuesRef.current = {
                                contactName: data.contactName,
                                email: data.email,
                                phoneNumber: data.phoneNumber,
                            };
                        }

                        return res;
                    })
                    .catch((err) => {
                        setIsLoading(false);
                        WalToast.error(err?.response?.data?.message || t('Common.Something_went_wrong'));

                        return err;
                    });
            } else {
                setIsLoading(true);
                await createShipperContact(payload)
                    .then((res) => {
                        if (res?.data) {
                            getShipper();
                            handleSaveContact(res);
                            // Reset form with saved values so it's no longer dirty
                            const savedValues = {
                                contactName: data.contactName,
                                email: data.email,
                                phoneNumber: data.phoneNumber,
                            };
                            reset(savedValues);
                            // Update initial values so form is no longer considered changed
                            initialValuesRef.current = {
                                contactName: data.contactName,
                                email: data.email,
                                phoneNumber: data.phoneNumber,
                            };
                        }
                    })
                    .catch((err) => {
                        setIsLoading(false);
                        WalToast.error(err?.response?.data?.message || t('Common.Something_went_wrong'));

                        return err;
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        } catch (error) {
            console.error('Error saving contact:', error);
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Command + Enter (Mac) or Ctrl + Enter (Windows/Linux)
            if ((event.metaKey || event.ctrlKey) && (event.key === 'Enter' || event.key === 'Return')) {
                if (!hasFormChanged || isLoading) {
                    event.preventDefault();

                    return;
                }
                event.preventDefault();
                handleSubmit(handleSave)();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleSubmit, handleSave, hasFormChanged, isLoading]);

    return (
        <>
            <div className="flex flex-wrap -mx-1 -my-1.5">
                <Controller
                    name="contactName"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <InputText
                            inputName="contact_name"
                            placeholder={t('Common.Enter_contact_name')}
                            parentClassName="w-full px-1 py-1.5"
                            value={value}
                            onChangeFunc={handleContactNameChange}
                            isError={!!error}
                            required
                        />
                    )}
                />
                <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <PhoneInput
                            name="phone"
                            phoneInputParentClassName={`w-1/2 px-1 py-1.5`}
                            onNumberChange={handlePhoneNumberChange}
                            isPhoneNumberValue={isPhoneNumberValue}
                            setIsPhoneNumberValue={setIsPhoneNumberValue}
                            isShowErrorMsg={false}
                            value={value}
                            errors={!!error}
                            maxLength={15}
                            dropdownDelayClassname="!top-[unset] bottom-full"
                        />
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <InputText
                            inputName="email"
                            placeholder={t('Common.Enter_email')}
                            value={value}
                            className={``}
                            parentClassName="w-1/2 px-1 py-1.5"
                            onChangeFunc={handleEmailChange}
                            isError={!!error}
                        />
                    )}
                />
            </div>
            <div ref={saveButtonRef} className="flex justify-end gap-x-2">
                <ButtonCmp className="btn-link-secondary" size="none" onClick={handleEditContact(false)}>
                    {t('Common.Cancel')}
                </ButtonCmp>
                <ButtonCmp
                    className="btn_primary"
                    iconSide="right"
                    onClick={handleSubmit(handleSave)}
                    loading={isLoading}
                    disabled={isLoading || !hasFormChanged}
                    icon={
                        <>
                            <Command className="w-4 h-4" />
                            <span>+</span>
                            <CornerDownLeft className="w-4 h-4" />
                        </>
                    }
                >
                    {t('Common.Save')}
                </ButtonCmp>
            </div>
        </>
    );
};

export default ShipperContactForm;
